<template>
  <div class="bookings-dashboard" :class="{'lister-branding': !isHirer}">
    <div class="bookings-dashboard-left-container">
      <div class="bookings-dashboard-heading" v-if="isHirer">Hi{{ user ? ' ' + user.first_name : ''}}, start your search here</div>
      <inline-location-search class="hirer-tools" v-if="isHirer" />
      <div class="findspace-nav-container">
        <div class="tabbed-nav-container">
          <div class="tabbed-nav" :class="{active: activeNav == 'active'}" @click="activeNav = 'active'">Active</div>
          <div class="tabbed-nav" :class="{active: activeNav == 'completed'}" @click="activeNav = 'completed'">Completed</div>
          <div class="tabbed-nav" :class="{active: activeNav == 'on-hold'}" @click="activeNav = 'on-hold'">On Hold</div>
          <div class="tabbed-nav" :class="{active: activeNav == 'cancelled'}" @click="activeNav = 'cancelled'">Cancelled / Expired</div>
          <div class="tabbed-nav filters">
            <select-wrapper
              :append-to-body="false"
              :options="[
                {id: 'booking', label: 'Bookings'},
                {id: 'recce', label: 'Recces'},
              ]"
              v-model="filterBy"
              class="select-filter"
              placeholder="Filter by"
              @input="getBookings()"
            ></select-wrapper>
            <select-wrapper
              :append-to-body="false"
              :options="[
                {id: 'name-asc', label: 'Name Asc'},
                {id: 'name-desc', label: 'Name Desc'},
                {id: 'date-asc', label: 'Date Asc'},
                {id: 'date-desc', label: 'Date Desc'},
              ]"
              v-model="orderBy"
              class="select-sort"
              placeholder="Sort by"
              @input="getBookings()"
            ></select-wrapper>
          </div>
        </div>
      </div>
      <div class="mobile-navigation">
        <div class="mobile-nav-item" :class="{'active': mobileView == 'lists'}" @click="mobileView = 'lists'">Lists</div>
        <div class="mobile-nav-item" :class="{'active': mobileView == 'calendar'}" @click="mobileView = 'calendar'">Calendar</div>
      </div>
      <div class="bookings-left-mobile" :class="{'active': mobileView == 'lists'}">
        <div>
          <loader :loading="loading" :fixed="true"></loader>
          <div class="bookings-container" v-if="!loading && bookings">
            <div class="booking-card" v-for="booking in bookings.data">
              <div class="booking-top-container">
                <div class="booking-top">
                  <div class="booking-label" :class="booking.type+'-type'">{{ booking.booking_type_label }}</div>
                  <div class="booking-delete interactive" @click="openCancelModal(booking)">
                    <svg xmlns="http://www.w3.org/2000/svg">
                      <image xlink:href="/images/icons/remove.svg" />
                    </svg>
                  </div>
                </div>
                <div class="booking-title interactive" @click="goToListing(booking)">{{ booking.listing.space_name }}</div>
                <div class="booking-address">{{ booking.listing.address_label }}</div>
                <div class="booking-info-container">
                  <div class="quote-container" v-if="booking.type === 'booking' && isHirer">
                    <div class="booking-info-label">Current Quote</div>
                    <div class="booking-info-value" v-if="booking.current_quote_for_user && booking.current_quote_for_user.grand_total_formatted !== null">
                      ${{ booking.current_quote_for_user.grand_total_formatted }}
                      <br />
                      (inc GST)
                    </div>
                    <div class="booking-info-value" v-else>TBC</div>
                  </div>
                  <div class="quote-container" v-else-if="booking.current_quote_total_formatted && isLister">
                    <div class="booking-info-label">Payment to You</div>
                    <div class="booking-info-value">
                      {{ booking.current_quote_total_formatted }}
                    </div>
                  </div>
                  <div class="booking-type-date-container">
                    <div class="booking-info-label">{{ booking.type === 'recce' ? 'Recce' : 'Production' }} Date</div>
                    <div class="booking-info-value">
                      <template v-if="booking.from !== booking.to">
                        {{ formatDate(booking.from, 'D MMM YYYY') }}
                        <br />
                        {{ formatDate(booking.to, 'D MMM YYYY') }}
                      </template>
                      <template v-else>
                        {{ formatDate(booking.from, 'D MMM YYYY') }}
                      </template>
                    </div>
                  </div>
                  <div class="booking-time-container">
                    <template v-if="booking.type === 'recce' && booking.call_times_arrivals.length">
                      <div class="booking-info-label">{{ booking.type === 'recce' ? 'Recce' : 'Production' }} Time</div>
                      <div class="booking-info-value">
                        {{ booking.type === 'recce' ? booking.call_times_arrivals[0].toUpperCase() : formatTime(booking.call_times_arrivals[0], 'h:mma') }}
                      </div>
                    </template>
                  </div>
                </div>
                <p class="booking-info-label small mb-2" v-if="booking.type === 'booking' && booking.current_quote_for_user === null">
                  Our team will review your booking and confirm the price shortly
                </p>
              </div>
              <div class="booking-status-container" v-if="booking.status_log && booking.status_log.length">
                <div class="booking-status-info-container">
                  <template v-for="status in booking.status_log.slice(0, 1)" v-if="status.pending_action && status.pending_action.actions && status.pending_action.actions.length">
                    <div
                      v-for="option in status.pending_action.actions"
                      v-if="option.action"
                      class="booking-status-action badge interactive"
                      :class="[option.class]"
                      @click="doAction(option.action, booking)">
                      {{ option.label }}
                    </div>
                  </template>
                </div>
                <div class="booking-status-wrapper">
                  <template v-for="status in booking.status_log.slice(0, 1)">
                    <svg xmlns="http://www.w3.org/2000/svg">
                      <image xlink:href="/images/icons/ellipse-green.svg" />
                    </svg>
                    <div class="booking-status-name-container">
                      <div class="booking-status-name">{{ status.label }}</div>
                      <div class="booking-status-date">{{ status.date }}</div>
                    </div>
                    <div>&nbsp;</div>
                    <div class="booking-reason">{{ status.reason }}</div>
                  </template>
                </div>
                <div class="booking-details-btn interactive" @click="viewBooking(booking)">
                  View Details
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pagination" v-if="bookings && bookings.data && bookings.data.length">
          <pagination
            :current-page="page"
            :total-pages="totalPages"
            @change-page="changePage"
          ></pagination>

          <div class="pagination-label">
            {{ bookings.from }} - {{ bookings.to }} of
            {{ bookings.total }} bookings
          </div>
        </div>
      </div>
    </div>
    <div class="bookings-dashboard-right-container" :class="{'active': mobileView == 'calendar'}">
      <div class="lets-chat-block tablet-hidden">
        <div class="question-block">
          <svg xmlns="http://www.w3.org/2000/svg">
            <image xlink:href="/images/icons/message-question.svg" />
          </svg>
        </div>
        <div class="lets-chat-heading">Need something more?</div>
        <div class="lets-chat-text">Let us know what you need and we can assist you</div>
        <div class="lets-chat-btn-container">
          <button class="button lets-chat-btn-transparent" @click="openChat">Let's Chat</button>
        </div>
      </div>

      <div class="bookings-calendar" v-if="calendarBookings && calendarBookings.data">
        <vue-date-picker v-model="selectedDate" no-header inline @onChange="showDailyBookings" :allowedDates="checkBookingDate" />
      </div>
    </div>
  </div>
</template>

<script>
const customParseFormat = require('dayjs/plugin/customParseFormat')
dayjs.extend(customParseFormat)

export default {
  data() {
    return {
      activeNav: 'active',
      bookings: null,
      calendarBookings: null,
      page: 1,
      totalPages: 1,
      currentPath: null,
      loading: false,
      calendarLoading: false,
      selectedDate: null,
      showBookingTooltip: false,
      mobileView: 'lists',
      orderBy: null,
      filterBy: null,
    }
  },
  watch: {
    'activeNav': {
      handler: function() {
        this.getBookings();
        this.getCalendarBookings();
      }
    }
  },
  methods: {
    goToExplore() {
        window.location = '/explore'
    },
    changePage(which) {
      this.page = which;
      this.getBookings();
    },
    getBookings() {
      if (!this.loading) {
        this.loading = true
        this.bookings = []

        axios.get('/api/account/bookings/' + this.activeNav, { params: { page: this.page, orderBy: this.orderBy, filterBy: this.filterBy } })
          .then(response => {
            this.bookings = response.data
            this.totalPages = Math.ceil(response.data.total / 6);

            this.$nextTick(() => {
              this.loading = false
            })
          })
          .catch(() => {
            this.loading = false
          })
      }
    },
    getCalendarBookings() {
      if (!this.calendarLoading) {
        this.calendarLoading = true
        this.calendarBookings = []

        axios.get('/api/account/bookings/' + this.activeNav, {
          params: {
            page: 1,
          }
        })
          .then(response => {
            let data = response.data
            this.calendarBookings = data

            this.$nextTick(() => {
              this.calendarLoading = false
            })
          })
          .catch(() => {
            this.calendarLoading = false
          })

      }
    },
    openCancelModal (booking) {
      this.$store.dispatch('modals/show', {
        title: `Are you sure you want to cancel ${this.getFullBookingName(booking)}?`,
        type: 'booking-cancel',
        data: booking
      })
    },
    openChat() {
      this.$store.dispatch("intercom/openNewChat");
    },
    formatDate (src, format) {
      return dayjs(src).format(format)
    },
    formatTime (src, format) {
      return dayjs(src, 'HH:mm:ss').format(format)
    },
    getFullBookingName (booking) {
      return `${booking.booking_type_label} <span class="fg-primary">${booking.listing.space_name}</span>`
    },
    goToListing(booking) {
      window.open(booking.listing.listing_url, '_blank')
    },
    doAction (action, booking) {
      switch (action) {
        case 'review_and_pay':
          const title = booking.is_payable && booking.has_cost ? 'Review &amp; Pay' : 'Review'
          this.$store.dispatch('modals/show', {
            title: `${this.getFullBookingName(booking)} - ${title}`,
            type: 'booking-quote-review',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'pay':
          const payTitle = booking.is_payable && booking.has_cost ? 'Pay' : 'Review'
          this.$store.dispatch('modals/show', {
            title: `${this.getFullBookingName(booking)} - ${payTitle}`,
            type: 'hirer-quote-pay',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'review':
          const reviewTitle = booking.is_payable && booking.has_cost ? 'Review &amp; Pay' : 'Contact Admin'
          this.$store.dispatch('modals/show', {
            title: `${this.getFullBookingName(booking)} - ${reviewTitle}`,
            type: 'hirer-quote-confirm',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'view_quote':
          this.$store.dispatch('modals/show', {
            title: `${this.getFullBookingName(booking)} - Payout`,
            type: 'booking-lister-quote',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'view_detailed_quote':
          this.$store.dispatch('modals/show', {
            title: `${this.getFullBookingName(booking)} - Quote`,
            type: 'booking-hirer-quote-view',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'feedback':
          this.$store.dispatch('modals/show', {
            title: `Amend Booking for ${this.getFullBookingName(booking)}`,
            type: 'booking-feedback',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'confirm':
          if (!this.$store.getters['user/stripeConnectIsOnboarded'])
          {
            this.$store.dispatch('modals/show', {
              title: `Let's setup your payment details!`,
              type: 'first-payment-setup',
              data: booking
            })
              .then(() => {})
              .catch(() => {})
            break
          }

          this.$store.dispatch('modals/show', {
            title: `Confirm ${this.getFullBookingName(booking)}`,
            type: 'booking-confirm',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'approve_by_lister':
          if (!this.$store.getters['user/stripeConnectIsOnboarded'])
          {
            this.$store.dispatch('modals/show', {
              title: `Let's setup your payment details!`,
              type: 'first-payment-setup',
              data: booking
            })
              .then(() => {})
              .catch(() => {})
            break
          }

          this.$store.dispatch('modals/show', {
            title: `Confirm ${this.getFullBookingName(booking)}`,
            type: 'lister-quote-confirm',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'decline':
          this.$store.dispatch('modals/show', {
            title: `Decline ${this.getFullBookingName(booking)}`,
            type: 'booking-decline',
            data: booking
          })
            .then(() => {})
            .catch(() => {})
          break

        case 'download_schedule':
          if (!booking.booking_schedule_url) {
            return this.$store.dispatch('modals/show', {
              title: `${booking.booking_type_label} schedule & location`,
              type: 'booking-schedule-unavailable',
              data: booking
            })
              .then(() => {})
              .catch(() => {})
          }
          window.location.href = `${booking.booking_schedule_url}?token=${this.$store.getters['user/accessToken']}`
          break
      }
    },
    checkBookingDate(calendarDate) {
      return this.calendarBookings.data.filter(booking => {
        if(booking.to) {
          return new Date(booking.from) <= new Date(calendarDate)
            && new Date(booking.to) >= new Date(calendarDate)
        }
        return new Date(booking.from) == new Date(calendarDate)
      }).length > 0
    },
    showDailyBookings() {
      const tooltipBookings = this.calendarBookings.data.filter(booking => {
        if(booking.to) {
          return new Date(booking.from) <= new Date(this.selectedDate)
            && new Date(booking.to) >= new Date(this.selectedDate)
        }
        return new Date(booking.from) == new Date(this.selectedDate)
      })
      this.$store.dispatch('modals/show', {
        title: `Bookings for ${this.selectedDate}`,
        type: 'booking-daily-list',
        data: {'bookings': tooltipBookings},
        mini: true
      })
        .then(() => {})
        .catch(() => {})
    },
    viewBooking(booking) {
      this.$store.commit('findspace/setSubpage', { subpage: 'view', item: booking })
    },
  },
  mounted() {
    this.currentPath = window.location.pathname;
    this.getBookings();
    this.getCalendarBookings();
  },
  computed: {
    user () {
      return this.$store.getters['user/account']
    },
    isHirer () {
      return this.$store.getters['user/isHirer']
    },
    isLister () {
      return this.$store.getters['user/isLister']
    },
  }
}
</script>
